import { AsyncPipe, DecimalPipe, NgClass, NgFor, NgIf, NgOptimizedImage } from '@angular/common';
import { Component, inject, Input, OnChanges } from '@angular/core';
import { Router } from '@angular/router';
import { MenuItemSizeDto } from '../../../../api/v1/models/menu-item-size-dto';
import { MenuListRepository } from '../../../../shared/repositories/menu-list.repository';
import { ProductPriceComponent } from '../../../../blocks/product-price/product-price.component';
import { MatButtonModule } from '@angular/material/button';
import { Observable, shareReplay, Subject, tap } from 'rxjs';
import { ConfigRepository } from '../../../../shared/repositories/config.repository';
import { BasketRepository } from '../../../../shared/repositories/basket.repository';
import { CounterComponent } from '../../../../shared/components/counter/counter.component';
import { BasketItem } from '../../../../shared/interfaces/basket-item.interface';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ProductUnitComponent } from '../../../../blocks/product-unit/product-unit.component';
import { PictureFallbackDirective } from '../../../../shared/directives/picture-fallback/picture-fallback.directive';
import { combineLatestWith, first, map } from 'rxjs/operators';
import { UIMenuItemDto } from '../../../../shared/interfaces/ui-menu-item-dto.interface';
import { SessionRepository } from 'src/app/shared/repositories/session.repository';
import { ModifierItem } from '../../../../components/modifier-list/modifier-item.interface';
import { DynamicComponentService } from '../../../../shared/services/dynamic-component.service';
import { ObserverDirective } from 'src/app/shared/directives/observer/observer.directive';

interface IForm {
  counter: FormControl<number>;
}

@Component({
  selector: 'app-product-card',
  templateUrl: './product-card.component.html',
  styleUrls: ['./product-card.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    NgFor,
    NgClass,
    NgOptimizedImage,
    DecimalPipe,
    ProductPriceComponent,
    MatButtonModule,
    AsyncPipe,
    CounterComponent,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    PictureFallbackDirective,
    ProductUnitComponent,
    AsyncPipe,
    ObserverDirective
  ]
})
export class ProductCardComponent implements OnChanges {
  constructor(
    protected router: Router,
    protected configStore: ConfigRepository,
    protected basketStore: BasketRepository,
    protected menuRepository: MenuListRepository,
    protected sessionRepository: SessionRepository,
    protected dynamicComponent: DynamicComponentService,
  ) { }

  @Input() data: UIMenuItemDto;
  @Input() isLock: boolean = false;
  @Input() isHidePrice: boolean = false;

  private menuListRepository = inject(MenuListRepository);
  private hasModifiers: boolean;
  private cardAppearanceObserver$ = new Subject();

  energy: number;
  currentSize!: MenuItemSizeDto;
  defaultPrice$: Observable<number | null>;
  isBasketMode$: Observable<boolean> = this.configStore.isBasketMode$;
  stopListItems$: Observable<Map<string, number>> = this.menuRepository.stopListItems$;

  private prepareData(): void {
    if (this.data) {
      this.currentSize = this.menuListRepository.getDefaultSize(this.data);
      this.defaultPrice$ = this.menuListRepository.getDefaultPriceObservable(this.currentSize);
      const energy = this.currentSize.nutritionPerHundredGrams?.energy ?? 0;
      const weight = this.currentSize.portionWeightGrams ?? 0;
      this.energy = weight * energy / 100;
    }
  }

  form: FormGroup<IForm> = new FormGroup<IForm>({
    counter: new FormControl<number>(1, { nonNullable: true }),
  });

  basketItem: BasketItem | undefined;

  basketItem$: Observable<BasketItem | null> = this.basketStore.items$.pipe(
    map(items => {
      if (!items.length) { return null }
      return items.find(item => item.item.uniqKey === this.data.uniqKey) || null;
    }),
    tap(basketItem => {
      if (!basketItem) { return }
      this.form.controls.counter.patchValue(basketItem.count);
    }),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  isShowCounter$: Observable<boolean> = this.basketItem$.pipe(
    map(basketItem => !!(basketItem && basketItem.count))
  )

  isDisabled$ = this.cardAppearanceObserver$.pipe(
    first(),
    combineLatestWith(this.stopListItems$),
    map(([_, stopList]) => {
      return this.data.balance === 0 ||
        stopList && this.data.itemSizes.some(size =>
          size.itemModifierGroups.some(group =>
            group.childModifiersHaveMinMaxRestrictions
              ? group.items.some(modifier =>
                stopList.has(modifier.itemId) &&
                stopList.get(modifier.itemId)! < modifier.restrictions.minQuantity
              )
              : group.items.every(modifier => stopList.has(modifier.itemId)) &&
              group.restrictions.minQuantity >= group.items.reduce((totalValue, modifier) =>
                totalValue += stopList.get(modifier.itemId) ?? 0, 0
              )
          )
        )
    }),
  );

  ngOnChanges(): void {
    this.prepareData();

    this.data.itemSizes.map(item => {
      this.hasModifiers = !!item.itemModifierGroups.length;
    });
  }

  onAddToBasket(slug: string | null): void {
    if (!slug) { return }

    if (!this.hasModifiers) {
      this.addToBasket();
    } else {
      this.onOpenCard(slug);
    }
  }

  onOpenCard(slug: string | null): void {
    if (!slug) { return }

    this.menuRepository.setActiveCategory(this.data.categoryId);
    void this.router.navigate([`/category/${this.data.categorySlug}/${slug}`]);
  }

  addToBasket(): void {
    const count = 1;
    const modifiers: ModifierItem[] = [];

    this.basketStore.addItem({
      item: this.data,
      size: this.currentSize,
      count: count,
      modifiers: modifiers,
      appliedDiscount: 0,
      itemKey: '',
      totalPrice: this.sessionRepository.getItemTotal(this.currentSize.prices, modifiers, count)
    });
  }

  onChangeCount(itemKey: string, count: number): void {
    if (count === 0) {
      this.basketStore.removeItem(itemKey);
      return;
    }
    this.basketStore.updateCount(itemKey, count);
  }

  setLabelFilter(label: string) {
    this.sessionRepository.toggleShowFilterBar(true);
    this.sessionRepository.toggleActiveLabelFilter(label);
  }

  onAppear() {
    this.cardAppearanceObserver$.next(null);
  }
}
